import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Award, ChevronDown, ChevronUp, FileText, ExternalLink } from 'lucide-react'
import { PortalEmptyState } from '../ui/PortalEmptyState'
import type { JournalistProfileState } from './useJournalistProfile'

const WEIGHTS = [
  { label: 'Volume',        pts: 150, desc: 'Number of articles and reports submitted' },
  { label: 'Reach',         pts: 250, desc: 'Total audience reach across all published coverage' },
  { label: 'Breadth',       pts: 150, desc: 'Coverage across multiple institutions and topics' },
  { label: 'Recency',       pts: 100, desc: 'Consistency and recency of submissions' },
  { label: 'Accreditation', pts: 100, desc: 'Active accreditations held with institutions' },
]

export function ProfileCoverageTab({ state }: { state: JournalistProfileState }) {
  const { scoreBreakdown, submissions } = state
  const [infoOpen, setInfoOpen] = useState(false)

  return (
    <div className="space-y-5">
      {(scoreBreakdown ?? []).length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Award className="w-4 h-4 text-accent" />
                Coverage Score Breakdown
              </CardTitle>
              <button
                onClick={() => setInfoOpen(o => !o)}
                className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors"
              >
                How is my score calculated?
                {infoOpen ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              </button>
            </div>
          </CardHeader>
          <CardContent className="space-y-3">
            {(scoreBreakdown ?? []).map(item => {
              const pct = item.max_score > 0 ? Math.min((item.score / item.max_score) * 100, 100) : 0
              const barColor = pct >= 80 ? 'hsl(var(--success))' : pct >= 50 ? 'hsl(var(--accent))' : 'hsl(var(--destructive))'
              const label = item.component.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
              return (
                <div key={item.component}>
                  <div className="flex items-center justify-between text-[12px] mb-1">
                    <div>
                      <span className="font-medium">{label}</span>
                      <span className="text-muted-foreground ml-2 text-[11px]">{item.description}</span>
                    </div>
                    <span className="font-semibold tabular-nums shrink-0 ml-3">
                      {Math.round(item.score)} / {item.max_score}
                    </span>
                  </div>
                  <div className="h-2 bg-muted rounded-full overflow-hidden">
                    <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, backgroundColor: barColor }} />
                  </div>
                </div>
              )
            })}

            {infoOpen && (
              <div className="mt-4 pt-4 border-t border-border text-[11.5px] text-muted-foreground space-y-1.5">
                <p className="font-medium text-foreground text-[12px] mb-2">Scoring components and weights</p>
                {WEIGHTS.map(c => (
                  <div key={c.label} className="flex items-start gap-2">
                    <span className="font-semibold text-foreground w-24 shrink-0">{c.label}: {c.pts} pts</span>
                    <span className="text-justify">{c.desc}</span>
                  </div>
                ))}
                <p className="text-justify pt-1">Scores are recomputed automatically each night.</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="text-base">Submission History</CardTitle></CardHeader>
        <CardContent>
          {(submissions ?? []).length === 0 ? (
            <PortalEmptyState icon={FileText} message="No submissions yet." />
          ) : (
            <div className="space-y-2">
              {(submissions ?? []).map((s: any) => (
                <div key={s.id} className="flex items-center gap-3 p-3 rounded-lg border border-border transition-colors hover:border-accent/50">
                  <FileText className="w-4 h-4 text-muted-foreground shrink-0" />
                  <div className="flex-1 min-w-0">
                    <a
                      href={s.content_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm font-medium hover:underline inline-flex items-center gap-1 truncate max-w-full"
                    >
                      <span className="truncate">{s.content_url}</span>
                      <ExternalLink className="w-3 h-3 shrink-0" />
                    </a>
                    <div className="text-[11px] text-muted-foreground mt-0.5">
                      {s.organizations?.name ?? 'Unassigned'}
                      {s.submitted_at ? ` · ${new Date(s.submitted_at).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  {typeof s.similarity_score === 'number' && (
                    <span className="text-[11px] text-muted-foreground tabular-nums shrink-0">
                      {Math.round(s.similarity_score * 100)}% similar
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
